/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createFileRoute,
} from "@tanstack/react-router";
import { useAuth, useVenueDetailsBySlug } from "common/hooks";
import { useEffect, useMemo, useState } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion } from "common/config";
import VenueSheetContent from "@/components/VenueDetailSheet/components/VenueSheetContent";
import Loader from "@/components/common/Loader";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Helmet } from "react-helmet";
import { Storage } from "common/storage";
import AnalyticsService from "@/services/analytics";
import { useNearbyBusinesses } from "common/hooks/business/useNearbyBusinesses";

export type VenueDetailsRouteParams = {
  venueId: string; // from the route path "/venuedetails/$venueId"
};

export type VenueDetailsRouteSearch = {
  source: "map" | "activity";
  showAuthModal?: boolean;
};

export const Route = createFileRoute("/venuedetails/$venueId")({
  component: VenueDetails,
  validateSearch: (search: Record<string, unknown>): VenueDetailsRouteSearch => {
    return {
      source: search.source as "map" | "activity",
      showAuthModal: search.showAuthModal === "true" || search.showAuthModal === true,
    };
  },
  parseParams: (params: Record<string, string>) => {
    return {
      venueId: params.venueId,
    };
  },
});


function VenueDetails() {
  const { venueId: venueSlug } = Route.useParams();
  const { showAuthModal } = Route.useSearch();

  const { user, isLoading, isFetching } = useAuth();
  const isAuthLoading = isLoading || isFetching;

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();

  // load this venue from the server
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
  } = useVenueDetailsBySlug({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
  });

  const { showModal } = useAuthModals();
  
  // Add nearby businesses query
  const { data: nearbyBusinessesData, isLoading: isLoadingNearbyBusinesses } = useNearbyBusinesses(data?.details?.business.id.toString());
  // Add hasNearbyVenues constant
  const hasNearbyVenues = useMemo(() => {
    if (isLoadingNearbyBusinesses) return false;
    return (
      data?.details?.templateType !== "Basic" &&
      nearbyBusinessesData?.data?.nearbyBusinessCategories?.length > 0
    );
  }, [
    isLoadingNearbyBusinesses,
    data?.details?.templateType,
    nearbyBusinessesData,
  ]);

  useEffect(() => {
    if (location && !isAuthLoading) {
      loadVenueDetails();
    }
  }, [location, loadVenueDetails, user, isAuthLoading]);

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user]);

  useEffect(() => {
    if (data) {
      console.log("data", data);
      AnalyticsService.trackGAEventHandler("viewedVenue");
      AnalyticsService.trackMx("viewedVenue", {
        VenueId: data.details.id,
        VenueBrandId: data.details.brandId,
        VenueBrandName: data.details.brandName,
        VenueAddress: data.details.address,
        VenueName: data.details.name,
        VenueTypes: data.details.businessType,
      });
    }
  }, [data]);

  return (
    <div className="bg-white dark:bg-primary-900">
      <Helmet>
        <title>
          {data?.details?.name
            ? `${data.details.name.length > 30 ? data.details.name.substring(0, 30) + "..." : data.details.name} `
            : ""}
          | Accessibility Info | Sociability
        </title>
      </Helmet>
      {(isAuthLoading || isDetailsLoading) && <Loader />}
      <div className={`${!hasNearbyVenues ? 'max-w-[1160px] px-4 ' : 'px-8'} mx-auto overflow-hidden`}>      
        <section className="mb-2">
          {error && <div>{error.message}</div>}

          {data && (
            <VenueSheetContent
              venueDetailsReponse={data}
              onClose={() => {}}
              fullSize
              onRefresh={() => {
                setTimeout(() => loadVenueDetails(), 1000);
              }}
              openAllPhotosInline={false}
            />
          )}
        </section>
      </div>
    </div>
  );
}
